import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RCA_METHODS = [
  { value: '5-whys', label: '5-Whys', icon: 'HelpCircle', desc: 'Ask "why" five times to trace root cause' },
  { value: 'fishbone', label: 'Fishbone (Ishikawa)', icon: 'GitBranch', desc: 'Cause-and-effect diagram analysis' },
  { value: 'fault-tree', label: 'Fault Tree Analysis', icon: 'Network', desc: 'Top-down deductive failure analysis' },
  { value: 'other', label: 'Other Method', icon: 'MoreHorizontal', desc: 'Custom or alternative RCA method' },
];

const CONTRIBUTING_CATEGORIES = [
  'Human Factors', 'Equipment Failure', 'Process Deviation', 'Environmental Conditions',
  'Training Gap', 'Communication Failure', 'Management System', 'Design Deficiency',
  'Maintenance Failure', 'Procedural Non-compliance'
];

const RootCauseAnalysis = ({ rcaData, onRcaChange, readOnly = false }) => {
  const [newFactor, setNewFactor] = useState('');
  const [newWhy, setNewWhy] = useState('');

  const handleFieldChange = (field, value) => {
    onRcaChange?.({ ...rcaData, [field]: value });
  };

  const handleAddFactor = () => {
    if (!newFactor?.trim()) return;
    const updated = [...(rcaData?.contributingFactors || []), { id: Date.now(), text: newFactor?.trim(), category: '' }];
    onRcaChange?.({ ...rcaData, contributingFactors: updated });
    setNewFactor('');
  };

  const handleRemoveFactor = (id) => {
    onRcaChange?.({ ...rcaData, contributingFactors: rcaData?.contributingFactors?.filter(f => f?.id !== id) });
  };

  const handleAddWhy = () => {
    if (!newWhy?.trim()) return;
    const whys = [...(rcaData?.fiveWhys || []), { id: Date.now(), text: newWhy?.trim() }];
    onRcaChange?.({ ...rcaData, fiveWhys: whys });
    setNewWhy('');
  };

  const handleRemoveWhy = (id) => {
    onRcaChange?.({ ...rcaData, fiveWhys: rcaData?.fiveWhys?.filter(w => w?.id !== id) });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-6">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <Icon name="Search" size={18} className="text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Root Cause Analysis</h3>
      </div>

      {/* RCA Method */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">RCA Method *</label>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {RCA_METHODS?.map(method => (
            <button
              key={method?.value}
              onClick={() => !readOnly && handleFieldChange('rcaMethod', method?.value)}
              disabled={readOnly}
              className={`p-3 rounded-lg border text-left transition-all ${
                rcaData?.rcaMethod === method?.value
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30' :'border-border hover:border-primary/40 hover:bg-muted/50'
              } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <Icon name={method?.icon} size={16} className={rcaData?.rcaMethod === method?.value ? 'text-primary' : 'text-muted-foreground'} />
              <p className={`text-xs font-semibold mt-1.5 ${rcaData?.rcaMethod === method?.value ? 'text-primary' : 'text-foreground'}`}>
                {method?.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{method?.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* 5-Whys chain (shown when 5-whys selected) */}
      {rcaData?.rcaMethod === '5-whys' && (
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Why Chain</label>
          <div className="space-y-2">
            {rcaData?.fiveWhys?.map((why, idx) => (
              <div key={why?.id} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-2">
                  {idx + 1}
                </span>
                <div className="flex-1 p-2.5 bg-muted/40 rounded-lg text-sm text-foreground">{why?.text}</div>
                {!readOnly && (
                  <button onClick={() => handleRemoveWhy(why?.id)} className="mt-2 p-1 rounded hover:bg-red-100 hover:text-red-600 text-muted-foreground transition-colors">
                    <Icon name="X" size={13} />
                  </button>
                )}
              </div>
            ))}
            {!readOnly && (rcaData?.fiveWhys?.length || 0) < 5 && (
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center mt-2">
                  {(rcaData?.fiveWhys?.length || 0) + 1}
                </span>
                <input
                  type="text"
                  placeholder={`Why ${(rcaData?.fiveWhys?.length || 0) + 1}...`}
                  value={newWhy}
                  onChange={e => setNewWhy(e?.target?.value)}
                  onKeyDown={e => e?.key === 'Enter' && handleAddWhy()}
                  className="flex-1 text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                />
                <button onClick={handleAddWhy} className="px-3 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors">
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Findings */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Investigation Findings *</label>
        <textarea
          placeholder="Document detailed investigation findings, observations, and analysis..."
          value={rcaData?.findings || ''}
          onChange={e => handleFieldChange('findings', e?.target?.value)}
          readOnly={readOnly}
          rows={5}
          className="w-full text-sm px-3 py-2.5 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
        />
      </div>

      {/* Root Cause Summary */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Root Cause Summary *</label>
        <textarea
          placeholder="State the single, definitive root cause identified from the analysis..."
          value={rcaData?.rootCauseSummary || ''}
          onChange={e => handleFieldChange('rootCauseSummary', e?.target?.value)}
          readOnly={readOnly}
          rows={3}
          className="w-full text-sm px-3 py-2.5 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
        />
      </div>

      {/* Contributing Factors */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Contributing Factors</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {rcaData?.contributingFactors?.map(factor => (
            <span key={factor?.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-full">
              {factor?.text}
              {!readOnly && (
                <button onClick={() => handleRemoveFactor(factor?.id)} className="hover:text-red-600 transition-colors">
                  <Icon name="X" size={11} />
                </button>
              )}
            </span>
          ))}
        </div>
        {!readOnly && (
          <>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                placeholder="Add a contributing factor..."
                value={newFactor}
                onChange={e => setNewFactor(e?.target?.value)}
                onKeyDown={e => e?.key === 'Enter' && handleAddFactor()}
                className="flex-1 text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
              />
              <button onClick={handleAddFactor} className="px-3 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs text-muted-foreground mr-1">Quick add:</span>
              {CONTRIBUTING_CATEGORIES?.filter(c => !rcaData?.contributingFactors?.some(f => f?.text === c))?.slice(0, 5)?.map(cat => (
                <button
                  key={cat}
                  onClick={() => onRcaChange?.({ ...rcaData, contributingFactors: [...(rcaData?.contributingFactors || []), { id: Date.now(), text: cat }] })}
                  className="px-2 py-0.5 bg-muted hover:bg-muted/80 text-muted-foreground text-xs rounded-full transition-colors"
                >
                  + {cat}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RootCauseAnalysis;
